<script setup lang="ts">
import { useI18n } from 'vue-i18n'
const { t } = useI18n()
import { useLogger } from 'vue-logger-plugin'
import { setStoreLogger } from '../logger.ts'
import { AddItemToCart, DeleteItemFromCart } from '../services/CartService'
import { ref, onMounted } from 'vue'
import AoeWishlistImageButton from './AoeWishlistImageButton.vue'
import { GetBeeNumberAuthenicatedUser, GetBeeNumberSite, GetOrderTypeId, GetKitRecruiterSelectionUrl } from '../stores/SettingsStore.ts'
import { trackAddToCart } from '../analytics/index'
import { buildMatomoItemsFromCart } from '../analytics/matomoItemBuilder'
import { handleError } from '../services/ErrorService.ts'
import {SetIsLoading} from '../services/AoeHelpers.ts'
import { GetCurrentCartFromLocalStorage } from '../services/CartService'
import type { IAoeCart } from '../types/CartTypes.js'
import Swal from "sweetalert2";

const logger = useLogger()
setStoreLogger(logger)

const props = defineProps<{
  skucode: string
  displayquantityspinner?: string
  disabled?: boolean
  availablequantity?: string
  ordertypeidoverride?: string
  kitcategorycode?: string
  outofstockvisible?: boolean
}>()

const displayQuantitySpinner = props?.displayquantityspinner === 'true'
const orderTypeId = ref<string>('')
const quantity = ref(1)
const currentCart = ref<IAoeCart | null>(null)

async function addToCartClickedHandler() {
  logger.debug('addToCart', props)
  SetIsLoading(true)
  const beeNumberPayee = await GetBeeNumberSite()
  const beeNumberCustomer = await GetBeeNumberAuthenicatedUser()
  let canAddToCart = true

  // Check if this is a kit order type and if there's already a kit in the cart
  if (props.ordertypeidoverride) {
    currentCart.value = await GetCurrentCartFromLocalStorage()

    // Check if the cart already has items with the same kit order type
    if (currentCart.value && currentCart.value.orderTypeId === props.ordertypeidoverride && currentCart.value.items.length > 0) {
      logger.debug('Kit already exists in cart, removing all existing kit items before adding new one')

      // Remove all existing items from the kit cart
      try {
        for (const item of currentCart.value.items) {
          await DeleteItemFromCart({
            aoeCartIdToken: currentCart.value.aoeCartIdToken,
            lineNumber: item.lineNumber
          })
        }
        logger.debug('Successfully removed all existing kit items')
      } catch (error) {
        SetIsLoading(false)
        await handleError(error, t)
        return
      }
    }
  }

  // Skip availability check if OutOfStockVisible is true
  if(props.availablequantity && !props.outofstockvisible)
  {
    // Get available quantity synchronously
    const availableQtyPromise = GetAvailableQty()
    const availableQty = await availableQtyPromise
    if (availableQty < quantity.value) {
      canAddToCart = false
    }
  }

  if (canAddToCart)
  {
    AddItemToCart(null, beeNumberPayee, beeNumberCustomer, orderTypeId.value, props.skucode, quantity.value, undefined, !!props.ordertypeidoverride)
      .then(async () => {
        // Show success message
        await Swal.fire({
          title: t('alerts.AddedToCart'),
          icon: 'success',
          timer: 2000,
          showConfirmButton: false,
          position: 'top-end',
          toast: true
        });

        const freshCart = await GetCurrentCartFromLocalStorage()
        if (freshCart && freshCart.items && freshCart.items.length > 0) {
          const matomoItems = await buildMatomoItemsFromCart(freshCart, orderTypeId.value)
          trackAddToCart(matomoItems, freshCart.total ?? 0, freshCart.currencyId || 'EUR')
        }

      })
      .catch(async error => {
        await handleError(error, t)
      })
      .finally(async() => {
        SetIsLoading(false)
                // If this is a kit (has ordertypeidoverride), redirect to kit cart
        if (props.ordertypeidoverride) {
          let kitCartUrl = await GetKitRecruiterSelectionUrl();
          if (kitCartUrl) {
            // Add kit category code to URL if available
            if (props.kitcategorycode) {
              kitCartUrl += `?kitCategoryCode=${encodeURIComponent(props.kitcategorycode)}`
            }
            window.location.href = kitCartUrl;
          }
        }
      })
  }
  else
  {
    SetIsLoading(false)
    Swal.fire({
        title: t("alerts.AddToCartError"),
        text: t("alerts.NoQuantityAvailable"),
        icon: "error",
        confirmButtonText: t("buttons.Okay"),
      })
  }
}


async function GetAvailableQty()
{
  if(props.availablequantity)
  {
    currentCart.value = await GetCurrentCartFromLocalStorage()
    const currentCartQty = currentCart.value?.items.find(item => item.skuCode === props.skucode)?.quantity || 0
    return parseInt(props.availablequantity) - currentCartQty
  }
  return 0
}

async function plusClickedHandler() {
  logger.debug('plusClickedHandler')

  // Skip availability check if OutOfStockVisible is true
  if(props.availablequantity && !props.outofstockvisible)
  {
    const availableQty = await GetAvailableQty()
    if(quantity.value + 1 > availableQty) {
      quantity.value = availableQty === 0 ? 1 : availableQty
    }
    else {
      quantity.value = quantity.value + 1
    }
  }
  else
  {
    quantity.value = quantity.value + 1
  }
}

function minusClickedHandler() {
  logger.debug('minusClickedHandler')
  if (quantity.value > 1) {
    quantity.value = quantity.value - 1
  }
}

logger.debug(
  'AOECartImageButton.vue',
  props,
  displayQuantitySpinner,
  props.displayquantityspinner,
)

onMounted(async () => {
  if(!props.ordertypeidoverride)
  {
    orderTypeId.value = await GetOrderTypeId()
  }
  else
  {
      orderTypeId.value = props.ordertypeidoverride
  }
})

async function qtyChangeHandler(event: Event) {
  logger.debug('qtyChangeHandler', event)
  const target = event.target as HTMLInputElement
  const value = parseInt(target.value, 10)

  if(props.availablequantity)
  {
    const availableQty = await GetAvailableQty()

    if(value > availableQty) {
      quantity.value = availableQty
    }
    else if (value > 0) {
      quantity.value = value
    } else {
      quantity.value = 1
    }
  }
  else
  {
    if (value > 0) {
      quantity.value = value
    } else {
      quantity.value = 1
    }
  }
}

</script>
<template>
  <template v-if="displayQuantitySpinner">
    <div class="row prodDetailSection quantity-section" data-testid="quantity-spinner-section">
      <div class="col-12">
        <div class="number" data-testid="quantity-controls">
          <button id="minusBtn" class="minus" @click="minusClickedHandler" data-testid="quantity-minus-btn">
            <b>-</b>
          </button>
          <input id="qty" type="number" :value="quantity" @change="qtyChangeHandler($event)" data-testid="quantity-input" />
          <button id="plusBtn" class="plus" @click="plusClickedHandler" data-testid="quantity-plus-btn">
            +
          </button>
        </div>
      </div>
    </div>
    <div class="row prodDetailSection button-section" data-testid="add-to-cart-section">
      <div class="col-12">
        <div class="button-container">
          <div class="button-wrapper add-to-cart-wrapper">
            <button
              class="text-center btn primaryBtn btnFullWidth"
              :data-sku-code="props.skucode"
              :disabled="props.disabled ? props.disabled : false"
              @click="addToCartClickedHandler"
              data-testid="add-to-cart-btn"
            >
              {{ t('labels.AddToCart') }}
            </button>
          </div>
          <div class="button-wrapper wishlist-wrapper" data-testid="wishlist-button-area">
            <AoeWishlistImageButton
              :skucode="props.skucode"
              :isproductdetail="true"
              data-testid="wishlist-btn"
            ></AoeWishlistImageButton>
          </div>
        </div>
      </div>
    </div>
  </template>
  <template v-else>
    <div class="col-12 text-center btnAddToCart" data-testid="simple-add-to-cart-container">
      <button class="text-center btn primaryBtn" :disabled="disabled" @click="addToCartClickedHandler" data-testid="simple-add-to-cart-btn">
        {{ t('labels.AddToCart') }}
      </button>
    </div>
  </template>
</template>

<style lang="scss" scoped>
  .btnAddToCart {
      button.btn {
        font-weight: $font-weight-bold;
        border-radius: $button-radius;
      }
  }

  .row.prodDetailSection {
    padding-left: 0;
  }

  // Reduce spacing between quantity and button sections
  .quantity-section {
    padding-bottom: $spacing-2; // Reduce bottom padding from 20px to 8px
  }

  .button-section {
    padding-top: $spacing-2; // Reduce top padding from 20px to 8px
  }

  // Button container for consistent layout behavior
  .button-container {
    display: flex;
    flex-direction: column;
    gap: $spacing-3;
    align-items: stretch; // Mobile: full-width stacked

    // Medium+ screens: side-by-side, left-aligned with content above
    @media (min-width: 768px) {
      flex-direction: row;
      align-items: stretch; // Stretch buttons to match tallest height
      gap: $spacing-3;
      // No margin-left, no max-width — natural left alignment
    }
  }

  .button-wrapper {
    width: 100%; // Mobile: full width
    display: flex;
    align-items: stretch;
    justify-content: center;
    margin: 0 !important;
    padding: 0 !important;
    vertical-align: top !important;

    // Medium+ screens: equal width
    @media (min-width: 768px) {
      width: auto;
      flex: 1; // Equal width for both buttons
    }
  }

  // Ensure buttons fill their wrappers and center content
  .button-wrapper button,
  .button-wrapper .k-button,
  .button-wrapper .btn {
    width: 100%;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    height: 100% !important;
    box-sizing: border-box !important;
    margin: 0 !important;
    padding: 10px 20px !important;
    line-height: 1 !important;
    vertical-align: top !important;
  }

  // Add to cart button specific styles
  .add-to-cart-wrapper {
    button {
      width: 100%;
    }
  }

  // Wishlist wrapper specific styles
  .wishlist-wrapper {
    margin: 0 !important;
  }
</style>
